import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ExternalLink } from "lucide-react";
import RoosterLogo from "@/components/RoosterLogo";
import { useAuth, hasEverAuthenticated } from "@/contexts/AuthContext";
import NewsSubscribeForm from "@/components/NewsSubscribeForm";
import { runJob } from "@/lib/pollJob";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ArticleRow({ article, i }) {
  return (
    <a
      href={article.url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`news-preview-article-${i}`}
      className="block px-5 py-4 hover:bg-[#FAFAFA] transition-colors border-t border-neutral-200"
    >
      <div className="ticker-label !text-[10px] !font-bold">
        {article.source_name}
        {article.date_published ? ` · ${article.date_published}` : ""}
      </div>
      <div className="font-display text-lg font-black leading-snug tracking-tight mt-1">{article.headline}</div>
      <div className="text-[13px] text-neutral-700 mt-2 leading-snug">{article.summary}</div>
      <div className="ticker-label !text-[10px] mt-2 inline-flex items-center gap-1 border-b-2 border-[#FFD600] pb-0.5">
        Read the story <ExternalLink className="w-3 h-3" />
      </div>
    </a>
  );
}

export default function NewsLandingPage() {
  const [sections, setSections] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [lastPrompt, setLastPrompt] = useState("");
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const { search } = useLocation();

  // Auto-route returning users (mirrors LandingPage behavior).
  useEffect(() => {
    if (isLoading) return;
    const params = new URLSearchParams(search);
    if (params.get("stay") === "1") return;
    if (user) { navigate("/app", { replace: true }); return; }
    if (hasEverAuthenticated()) { navigate("/login", { replace: true }); }
  }, [isLoading, user, navigate, search]);

  const handlePreview = async (prompt, topics) => {
    const p = (prompt || "").trim();
    // topics is array of {label, prompt}
    const structured = (topics || []).filter((t) => t && (t.prompt || "").trim());
    if (!p && !structured.length) {
      toast.error("Lock in a news interest first.");
      return;
    }
    setPreviewing(true);
    try {
      const result = await runJob(`${API}/news/preview/start`, {
        session_id: `preview-${Date.now()}`,
        message: p,
        topics: structured.map((t) => t.prompt),
        topic_labels: structured.map((t) => t.label || ""),
        history: [],
      });
      setSections(result.sections || []);
      setLastPrompt(p);
      const total = (result.articles || []).length;
      toast.success(`Fetched ${total} fresh stories.`);
    } catch {
      toast.error("Couldn't fetch news right now.");
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      <header className="border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <RoosterLogo size={40} />
            <div className="font-display text-2xl font-black tracking-tighter">Weathervane</div>
          </Link>
          <div className="flex items-center gap-3 sm:gap-5">
            <Link to="/" data-testid="news-nav-weather" className="text-[11px] uppercase tracking-widest font-semibold border-b-2 border-transparent hover:border-[#FFD600] pb-0.5">Weather</Link>
            <div className="ticker-label hidden lg:block">AI-curated news, daily, by email</div>
            <NewsAuthNav />
          </div>
        </div>
      </header>

      <section className="relative">
        <div className="absolute inset-0 grid-overlay pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* LEFT — copy + form */}
          <div className="lg:col-span-7">
            <div className="ticker-label mb-6">Weathervane News</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]">
              News tuned
              <br />
              to{" "}
              <span className="relative inline-block">
                <span className="relative z-10">your</span>
                <span aria-hidden className="absolute left-0 -bottom-1 h-3 w-full bg-[#FFD600] z-0" />
              </span>
              {" "}signal.
            </h1>
            <p className="mt-8 text-base sm:text-lg text-neutral-700 max-w-xl leading-relaxed">
              Tell our AI what you actually care about, and every morning we&rsquo;ll search the live web for the <strong>6 most relevant stories for each topic</strong> — with sources, dates, and tight summaries you can scan in under 2 minutes.
            </p>
            <p className="mt-4 text-sm text-neutral-600 max-w-xl leading-relaxed">
              New: subscribe to the <strong>Top 10 Stocks</strong> — a daily consensus watch across Goldman, Morgan Stanley, JPMorgan, BofA and 8 other major desks. It ships at the top of your news email. <Link to="/app?tab=stocks" className="underline font-semibold">See today&rsquo;s list &rarr;</Link>
            </p>

            <NewsSubscribeForm onPreview={handlePreview} previewing={previewing} />
          </div>

          {/* RIGHT — live preview */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-6">
              <div className="border border-black bg-white" data-testid="news-email-preview">
                <div className="flex items-center justify-between px-5 py-3 border-b border-black bg-[#0A0A0A] text-white">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-[#FFD600]" />
                    <span className="ticker-label !text-white/80">Your daily news — preview</span>
                  </div>
                  <div className="ticker-label !text-white/60">
                    {sections ? "Live preview — Sonar" : "Sample"}
                  </div>
                </div>
                <div className="px-5 py-4 border-b border-neutral-200">
                  <div className="font-display text-2xl font-black tracking-tight">Good morning.</div>
                  <div className="text-[12px] text-neutral-600 mt-1">
                    AI-curated news based on your interest
                    {lastPrompt && (
                      <>: <span className="italic text-neutral-800">&ldquo;{lastPrompt.length > 90 ? lastPrompt.slice(0, 90) + '…' : lastPrompt}&rdquo;</span></>
                    )}
                  </div>
                </div>
                {!sections ? (
                  <div className="px-5 py-10 text-center">
                    <div className="ticker-label mb-2 !text-[10px]">Sample</div>
                    <div className="text-[13px] text-neutral-600 leading-relaxed">
                      Lock in your news interest on the left → click <span className="font-semibold">&ldquo;Preview news&rdquo;</span> to see live stories matching your prompt, right here. 6 per topic.
                    </div>
                  </div>
                ) : sections.length === 0 || !sections.some((s) => s.articles?.length) ? (
                  <div className="px-5 py-10 text-center text-[13px] text-neutral-600">
                    Nothing recent matched. Try refining your interest.
                  </div>
                ) : (
                  sections.map((sec, si) => {
                    const showHeader = sections.length > 1 || !!sec.label;
                    return (
                      <div key={`sec-${si}`} data-testid={`news-section-${si}`}>
                        {showHeader && (
                          <div className="px-5 pt-6 pb-3 border-t-2 border-black bg-[#FFFBE6]">
                            <div className="inline-block bg-black text-[#FFD600] font-display text-[10px] font-black tracking-[0.18em] px-2 py-1 uppercase">Topic {si + 1} of {sections.length}</div>
                            <div className="font-display text-2xl font-black tracking-tight mt-2" data-testid={`news-section-label-${si}`}>{sec.label}</div>
                            <div className="ticker-label !text-[10px] mt-1">{sec.articles.length} {sec.articles.length === 1 ? "story" : "stories"}</div>
                          </div>
                        )}
                        {(sec.articles || []).map((a, i) => (
                          <ArticleRow key={`${si}-${a.url}-${i}`} article={a} i={`${si}-${i}`} />
                        ))}
                      </div>
                    );
                  })
                )}
              </div>
              <div className="mt-4 px-1 text-[11px] text-neutral-500 leading-relaxed">
                Powered by Perplexity Sonar + Claude for interest refinement. Stories are fetched fresh every morning; we never cache stale news.
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-black/10 mt-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <RoosterLogo size={28} />
            <span className="font-display font-black">Weathervane News</span>
          </div>
          <div className="text-[11px] text-neutral-600 max-w-2xl sm:text-right leading-relaxed">
            © {new Date().getFullYear()} Weathervane, LLC. &ldquo;Weathervane&rdquo; and the Weathervane rooster design are registered trademarks of Weathervane, LLC. All Rights Reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}

function NewsAuthNav() {
  const { user } = useAuth();
  if (user) {
    return (
      <Link
        to="/app"
        data-testid="news-nav-dashboard"
        className="inline-flex items-center px-3 py-1.5 bg-black text-[#FFD600] hover:bg-[#FFD600] hover:text-black border border-black text-[10px] uppercase tracking-[0.16em] font-bold"
      >
        My dashboard
      </Link>
    );
  }
  return (
    <>
      <Link to="/login" data-testid="news-nav-signin" className="text-[11px] uppercase tracking-widest font-semibold border-b-2 border-transparent hover:border-[#FFD600] pb-0.5">Sign in</Link>
    </>
  );
}

